import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useState } from 'react'

import { StatusDot } from '../components/StatusDot'
import { CALENDAR_VIEW, SOURCE_STATUS } from '../labels'
import { monthLabel, periodOf, shortDate, todayIso } from '../lib/format'
import type { CycleView, SourceAvailability, SourceCell, Sprint } from '../lib/types'
import { MonthView } from './MonthView'
import { SourceCards } from './SourceCards'
import {
  BAND,
  BAND_SOFT,
  FONT_MONO,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  LINE_SOFT,
  PAPER,
  STATUS_COLOR,
  STICKER,
} from '../theme'

interface Props {
  cells: SourceCell[]
  /** Ficha por fuente de la vista de texto; la arma el backend. */
  fichas: SourceAvailability[]
  cycles: CycleView[]
  sprints: Sprint[]
  today: string
  onSelect: (cell: SourceCell) => void
}

type Mode = 'text' | 'lanes' | 'month' | 'table'

/** Ancho de la columna de nombres; lo comparten las líneas y su eje. */
const NAME_W = 172

/**
 * Calendario maestro, en cuatro lecturas del mismo dato.
 *
 * "Fichas" es la vista por defecto porque la pregunta diaria resultó ser más
 * simple que la trayectoria: *esta fuente, ¿hasta cuándo está en cada producto,
 * qué hay en SharePoint y vamos tarde?*. Eso en la vista de líneas había que
 * reconstruirlo de puntos de colores mes por mes, cruzándolo de memoria con qué
 * ciclo usa qué fuente. "Líneas" sigue siendo la mejor para ver trayectoria
 * —cómo viene una fuente, qué se atrasó, dónde estamos parados—. "Mes" baja al detalle:
 * la fecha exacta de cada entrega, la ventana de los ciclos y el sprint sobre
 * el mismo eje de días. "Tabla" sigue ahí para la pregunta de corte: qué pasó
 * exactamente en julio, con fechas.
 */
export function CalendarView({ cells, fichas, cycles, sprints, today, onSelect }: Props) {
  const [mode, setMode] = useState<Mode>('text')
  const [month, setMonth] = useState<string>(() => periodOf(todayIso()))

  const periods = [...new Set(cells.map((c) => c.period))].sort()
  const sources = [...new Set(cells.map((c) => c.source_id))]
  const nameOf = new Map(cells.map((c) => [c.source_id, c.source_name]))
  const byKey = new Map(cells.map((c) => [`${c.source_id}|${c.period}`, c]))

  // Los meses navegables salen de las fechas reales, no de un rango fijo: no
  // tiene sentido dejar avanzar a un mes del que no hay nada que contar.
  const dated = cells
    .flatMap((c) => [c.expected_date, c.actual_date])
    .filter((d): d is string => Boolean(d))
    .map(periodOf)
  const known = [...new Set([...dated, ...periods, periodOf(todayIso())])].sort()
  const bounds = { min: known[0], max: known[known.length - 1] }

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1.5}
        sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${LINE}` }}
      >
        <Box>
          <Typography variant="h2">{CALENDAR_VIEW.title}</Typography>
          <Typography
            sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT, mt: 0.25 }}
          >
            {mode === 'text'
              ? CALENDAR_VIEW.hintText
              : mode === 'lanes'
                ? CALENDAR_VIEW.hint
                : mode === 'month'
                  ? CALENDAR_VIEW.hintMonth
                  : CALENDAR_VIEW.hintTable}
          </Typography>
        </Box>
        <ModeToggle mode={mode} onChange={setMode} />
      </Stack>

      {cells.length === 0 ? (
        <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.75rem', color: INK_FAINT }}>
            {CALENDAR_VIEW.empty}
          </Typography>
        </Box>
      ) : mode === 'text' ? (
        <SourceCards fichas={fichas} byKey={byKey} onSelect={onSelect} />
      ) : mode === 'lanes' ? (
        <Lanes
          periods={periods}
          sources={sources}
          nameOf={nameOf}
          byKey={byKey}
          onSelect={onSelect}
        />
      ) : mode === 'month' ? (
        <MonthView
          cells={cells}
          cycles={cycles}
          sprints={sprints}
          period={month < bounds.min ? bounds.min : month > bounds.max ? bounds.max : month}
          bounds={bounds}
          today={today}
          onPeriodChange={setMonth}
          onSelect={onSelect}
        />
      ) : (
        <Grid
          periods={periods}
          sources={sources}
          nameOf={nameOf}
          byKey={byKey}
          onSelect={onSelect}
        />
      )}

      {mode !== 'month' && mode !== 'text' && <Legend />}
    </Paper>
  )
}

function ModeToggle({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  const options: [Mode, string][] = [
    ['text', CALENDAR_VIEW.modeText],
    ['lanes', CALENDAR_VIEW.modeLanes],
    ['month', CALENDAR_VIEW.modeMonth],
    ['table', CALENDAR_VIEW.modeTable],
  ]
  return (
    <Stack
      direction="row"
      sx={{ bgcolor: BAND, border: `1px solid ${LINE}`, borderRadius: 999, p: 0.4, gap: 0.4 }}
    >
      {options.map(([key, label]) => (
        <ButtonBase
          key={key}
          onClick={() => onChange(key)}
          sx={{
            borderRadius: 999,
            px: 1.75,
            py: 0.6,
            bgcolor: mode === key ? PAPER : 'transparent',
            boxShadow: mode === key ? STICKER : 'none',
            border: `1px solid ${mode === key ? LINE : 'transparent'}`,
            fontFamily: FONT_MONO,
            fontSize: '0.6875rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: mode === key ? INK : INK_SOFT,
            transition: 'background-color 180ms, color 180ms',
          }}
        >
          {label}
        </ButtonBase>
      ))}
    </Stack>
  )
}

interface GridProps {
  periods: string[]
  sources: string[]
  nameOf: Map<string, string>
  byKey: Map<string, SourceCell>
  onSelect: (c: SourceCell) => void
}

/**
 * Vista de líneas: una cinta por fuente, un punto por mes de datos.
 *
 * La cinta es neutra a propósito. El color entra sólo en los puntos, así que
 * el ojo recorre la fila buscando manchas rojas en vez de descifrar un
 * vitral — que es exactamente lo que pasaba con la rejilla pintada.
 */
function Lanes({ periods, sources, nameOf, byKey, onSelect }: GridProps) {
  const step = 100 / periods.length
  const at = (i: number) => (i + 0.5) * step

  const todayPeriod = todayIso().slice(0, 7)
  const todayIndex = periods.indexOf(todayPeriod)

  return (
    <Box sx={{ px: 3, py: 3, overflowX: 'auto' }}>
      <Box sx={{ minWidth: 640 }}>
        {/* Eje superior: los meses, una sola vez. */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 1.5 }}>
          <Box sx={{ width: NAME_W, flexShrink: 0 }} />
          <Box sx={{ position: 'relative', flex: 1 }}>
            {periods.map((p, i) => (
              <Box
                key={p}
                sx={{
                  position: 'absolute',
                  left: `${at(i)}%`,
                  transform: 'translateX(-50%)',
                  fontFamily: FONT_MONO,
                  fontSize: '0.625rem',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: i === todayIndex ? INK : INK_FAINT,
                  fontWeight: i === todayIndex ? 600 : 400,
                  whiteSpace: 'nowrap',
                }}
              >
                {monthLabel(p)}
              </Box>
            ))}
            <Box sx={{ height: 14 }} />
          </Box>
        </Box>

        <Box sx={{ position: 'relative' }}>
          <Stack spacing={0.75}>
            {sources.map((sid) => (
              <Box key={sid} sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={nameOf.get(sid) ?? sid}>
                  <Typography
                    sx={{
                      width: NAME_W,
                      flexShrink: 0,
                      pr: 2,
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: INK,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {nameOf.get(sid)}
                  </Typography>
                </Tooltip>

                <Box
                  sx={{
                    position: 'relative',
                    flex: 1,
                    height: 32,
                    borderRadius: 999,
                    bgcolor: BAND_SOFT,
                    border: `1px solid ${LINE_SOFT}`,
                    overflow: 'hidden',
                    transition: 'background-color 180ms',
                    '&:hover': { bgcolor: BAND },
                  }}
                >
                  {todayIndex >= 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${at(todayIndex)}%`,
                        top: 0,
                        bottom: 0,
                        width: 2,
                        ml: '-1px',
                        backgroundImage: `repeating-linear-gradient(to bottom, ${INK} 0 4px, transparent 4px 8px)`,
                        opacity: 0.45,
                        pointerEvents: 'none',
                      }}
                    />
                  )}

                  {periods.map((p, i) => {
                    const cell = byKey.get(`${sid}|${p}`)
                    return (
                      <Box
                        key={p}
                        sx={{
                          position: 'absolute',
                          left: `${at(i)}%`,
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'grid',
                          placeItems: 'center',
                          // Por encima de la línea de hoy: el punto nunca se parte.
                          zIndex: 1,
                        }}
                      >
                        {cell ? (
                          <LaneDot cell={cell} onSelect={onSelect} />
                        ) : (
                          <Box
                            sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: LINE }}
                          />
                        )}
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        {todayIndex >= 0 && (
          <Box sx={{ display: 'flex', mt: 1.25 }}>
            <Box sx={{ width: NAME_W, flexShrink: 0 }} />
            <Box sx={{ position: 'relative', flex: 1, height: 18 }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: `${at(todayIndex)}%`,
                  transform: 'translateX(-50%)',
                  bgcolor: INK,
                  color: PAPER,
                  borderRadius: 999,
                  px: 1,
                  py: 0.15,
                  fontFamily: FONT_MONO,
                  fontSize: '0.5625rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                }}
              >
                {CALENDAR_VIEW.today}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

function LaneDot({ cell, onSelect }: { cell: SourceCell; onSelect: (c: SourceCell) => void }) {
  const muted = cell.status === 'not_applicable'
  return (
    <StatusDot
      status={cell.status}
      size={muted ? 7 : 13}
      ring={cell.is_blocking}
      label={tooltipFor(cell)}
      onClick={() => onSelect(cell)}
    />
  )
}

/** Vista de tabla: la rejilla de siempre, con las fechas a la vista. */
function Grid({ periods, sources, nameOf, byKey, onSelect }: GridProps) {
  return (
    <TableContainer sx={{ maxHeight: '62vh' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, minWidth: NAME_W }}>
              {CALENDAR_VIEW.source}
            </TableCell>
            {periods.map((p) => (
              <TableCell key={p} align="center" sx={{ minWidth: 92 }}>
                {monthLabel(p)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sources.map((sid) => (
            <TableRow key={sid} hover>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  bgcolor: PAPER,
                  zIndex: 2,
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                }}
              >
                {nameOf.get(sid)}
              </TableCell>
              {periods.map((p) => {
                const cell = byKey.get(`${sid}|${p}`)
                return (
                  <TableCell key={p} align="center" sx={{ p: 0.5 }}>
                    {cell ? <CalendarCell cell={cell} onSelect={onSelect} /> : null}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function CalendarCell({ cell, onSelect }: { cell: SourceCell; onSelect: (c: SourceCell) => void }) {
  const color = STATUS_COLOR[cell.status] ?? '#CFC5B7'
  const muted = cell.status === 'not_applicable'

  return (
    <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{tooltipFor(cell)}</Box>}>
      <Box
        onClick={() => onSelect(cell)}
        sx={{
          cursor: 'pointer',
          borderRadius: 999,
          py: 0.7,
          px: 0.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.35,
          bgcolor: muted ? 'transparent' : `${color}12`,
          // El borde marca la ruta crítica: la fuente que detiene un ciclo.
          border: cell.is_blocking ? `1.5px solid ${color}` : '1.5px solid transparent',
          transition: 'background-color 160ms',
          '&:hover': { bgcolor: `${color}24` },
        }}
      >
        <StatusDot status={cell.status} size={8} />
        <Box
          component="span"
          sx={{
            fontFamily: FONT_MONO,
            fontSize: '0.625rem',
            color: muted ? INK_FAINT : INK_SOFT,
            whiteSpace: 'nowrap',
          }}
        >
          {cell.expected_date ? shortDate(cell.expected_date) : '—'}
        </Box>
      </Box>
    </Tooltip>
  )
}

function tooltipFor(cell: SourceCell): string {
  return [
    `${cell.source_name} · ${cell.period_label}`,
    `${CALENDAR_VIEW.expected}: ${shortDate(cell.expected_date)}`,
    cell.actual_date ? `${CALENDAR_VIEW.actual}: ${shortDate(cell.actual_date)}` : null,
    SOURCE_STATUS[cell.status],
    cell.detail,
    cell.is_blocking ? `⚠ ${CALENDAR_VIEW.blocking}` : null,
  ]
    .filter(Boolean)
    .join('\n')
}

function Legend() {
  const shown = [
    'upcoming',
    'expected_today',
    'available',
    'validated',
    'delayed',
    'action_required',
    'check_failed',
    'unconfigured',
  ]
  return (
    <Stack
      direction="row"
      spacing={2.25}
      flexWrap="wrap"
      useFlexGap
      sx={{ px: 3, py: 2, borderTop: `1px solid ${LINE}`, bgcolor: BAND_SOFT }}
    >
      {shown.map((s) => (
        <Stack key={s} direction="row" spacing={0.75} alignItems="center">
          <StatusDot status={s} size={8} />
          <Box
            component="span"
            sx={{
              fontFamily: FONT_MONO,
              fontSize: '0.625rem',
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              color: INK_SOFT,
            }}
          >
            {SOURCE_STATUS[s]}
          </Box>
        </Stack>
      ))}
    </Stack>
  )
}
